import { useState, useCallback, memo, useMemo } from "react"
import { useMutation, useQuery } from "@apollo/client"
import { Badge, Box, Button, HStack, Input, Spinner, Stack, Table, Text, VStack, IconButton } from "@chakra-ui/react"
import { FaSort, FaSortUp, FaSortDown, FaCopy } from "react-icons/fa6"
import Dialog from "@hydrologiq/shared/chakra/Dialog"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"
import { CreateButton, UpdateButton } from "@hydrologiq/shared/content/components/Button"
import {
  GetAllOrganizationsDocument,
  GetAllTenantsDocument,
  LinkOrganizationToTenantDocument,
  UpdateOrganizationLimitDocument,
  RemoveOrganizationLimitDocument,
} from "@hydrologiq/gql-experience/graphql"
import { createOrganization } from "../../auth/auth"
import { AdvancedOrganizationSettings } from "./AdvancedOrganizationSettings"
import { ManageLimitsDialog } from "./ManageLimitsDialog"
import { ManageSectionsDialog } from "./ManageSectionsDialog"
import { OrganizationFeaturesManager } from "../../components/admin/OrganizationFeaturesManager"
import { ManageAlertsDialog } from "../../components/admin/ManageAlertsDialog"

// Re-export for tests
export const GET_ALL_ORGANIZATIONS = GetAllOrganizationsDocument
export const LINK_ORGANIZATION_TO_TENANT = LinkOrganizationToTenantDocument
export const UPDATE_ORGANIZATION_LIMIT = UpdateOrganizationLimitDocument

const GET_ALL_TENANTS = GetAllTenantsDocument
const REMOVE_ORGANIZATION_LIMIT = RemoveOrganizationLimitDocument

interface OrganizationTenant {
  id: string
  name: string
  enabled: boolean
}

interface Organization {
  id: string
  name: string
  slug: string
  enabled: boolean
  tenants: OrganizationTenant[]
  seatLimit?: number | null
  memberCount: number
  createdAt: string
}

type SortField = "name" | "members" | "created"
type SortDirection = "asc" | "desc"

// Helper function to generate URL-safe slug from name
export const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
}

// Helper function to format date
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
}

// Helper function to copy to clipboard
const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text)
  toaster.success({ title: "Copied to clipboard" })
}

// Memoized dialog content for creating organization
const CreateOrganizationDialogContent = memo<{
  creating: boolean
  onSubmit: (data: { name: string; slug: string }) => void
  onCancel: () => void
}>(({ creating, onSubmit, onCancel }) => {
  const [name, setName] = useState("")
  const [slug, setSlug] = useState("")
  const [manualSlug, setManualSlug] = useState(false)

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newName = e.target.value
      setName(newName)
      if (!manualSlug) {
        setSlug(generateSlug(newName))
      }
    },
    [manualSlug]
  )

  const handleSlugChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setManualSlug(true)
    setSlug(e.target.value)
  }, [])

  const handleSubmit = useCallback(() => {
    if (!name.trim()) {
      toaster.error({ title: "Organization name is required" })
      return
    }
    if (!slug.trim()) {
      toaster.error({ title: "Organization slug is required" })
      return
    }
    onSubmit({ name: name.trim(), slug: slug.trim() })
  }, [name, slug, onSubmit])

  return (
    <VStack gap={4} align="stretch">
      <Box>
        <Text fontSize="sm" mb={2}>
          Organization Name
        </Text>
        <Input
          value={name}
          onChange={handleNameChange}
          placeholder="Enter organization name"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !creating) {
              handleSubmit()
            }
          }}
        />
      </Box>
      <Box>
        <Text fontSize="sm" mb={2}>
          Slug
        </Text>
        <Input value={slug} onChange={handleSlugChange} placeholder="auto-generated-slug" />
        <Text fontSize="xs" mt={1}>
          URL-safe identifier for the organization
        </Text>
      </Box>
      <HStack>
        <CreateButton onClick={handleSubmit} loading={creating}>
          Create Organization
        </CreateButton>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </HStack>
    </VStack>
  )
})

CreateOrganizationDialogContent.displayName = "CreateOrganizationDialogContent"

// Memoized dialog content for updating seat limit
const UpdateLimitDialogContent = memo<{
  organization: Organization
  updating: boolean
  onSubmit: (seatLimit: number | null) => void
  onCancel: () => void
}>(({ organization, updating, onSubmit, onCancel }) => {
  const [seatLimit, setSeatLimit] = useState<string>(organization.seatLimit ? organization.seatLimit.toString() : "")
  const [unlimited, setUnlimited] = useState(!organization.seatLimit)

  const handleSubmit = useCallback(() => {
    if (unlimited) {
      onSubmit(null)
    } else {
      const limit = parseInt(seatLimit, 10)
      if (isNaN(limit) || limit < 1) {
        toaster.error({ title: "Please enter a valid seat limit" })
        return
      }
      onSubmit(limit)
    }
  }, [seatLimit, unlimited, onSubmit])

  return (
    <VStack gap={4} align="stretch">
      <Text>
        Update seat limit for <strong>{organization.name}</strong>
      </Text>
      <Box>
        <Text fontSize="sm" mb={2}>
          Current Usage: {organization.memberCount} / {organization.seatLimit || "Unlimited"}
        </Text>
      </Box>
      <Box>
        <HStack mb={2}>
          <input
            type="checkbox"
            checked={unlimited}
            onChange={(e) => setUnlimited(e.target.checked)}
            id="unlimited-checkbox"
          />
          <Text fontSize="sm" as="label" htmlFor="unlimited-checkbox">
            Unlimited members
          </Text>
        </HStack>
        {!unlimited && (
          <Input
            type="number"
            value={seatLimit}
            onChange={(e) => setSeatLimit(e.target.value)}
            placeholder="Enter seat limit"
            min="1"
          />
        )}
      </Box>
      <HStack>
        <Button colorPalette="blue" onClick={handleSubmit} loading={updating}>
          Update Limit
        </Button>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </HStack>
    </VStack>
  )
})

UpdateLimitDialogContent.displayName = "UpdateLimitDialogContent"

export const OrganizationManagement = () => {
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [showAdvancedDialog, setShowAdvancedDialog] = useState(false)
  const [showManageLimitsDialog, setShowManageLimitsDialog] = useState(false)
  const [showManageSectionsDialog, setShowManageSectionsDialog] = useState(false)
  const [showManageOrgFeaturesDialog, setShowManageOrgFeaturesDialog] = useState(false)
  const [showManageAlertsDialog, setShowManageAlertsDialog] = useState(false)
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null)
  const [creating, setCreating] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [sortField, setSortField] = useState<SortField>("created")
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc")

  const { data: orgsData, loading: orgsLoading, refetch: refetchOrgs } = useQuery(GET_ALL_ORGANIZATIONS)
  const { data: tenantsData } = useQuery(GET_ALL_TENANTS)

  const [updateLimit, { loading: updating }] = useMutation(UPDATE_ORGANIZATION_LIMIT, {
    onCompleted: () => {
      toaster.success({ title: "Seat limit updated" })
      setSelectedOrganization(null)
      refetchOrgs()
    },
    onError: (error) => {
      toaster.error({ title: "Failed to update limit", description: error.message })
    },
  })

  const [removeLimit, { loading: removing }] = useMutation(REMOVE_ORGANIZATION_LIMIT, {
    onCompleted: () => {
      toaster.success({ title: "Seat limit removed" })
      setSelectedOrganization(null)
      refetchOrgs()
    },
    onError: (error) => {
      toaster.error({ title: "Failed to remove limit", description: error.message })
    },
  })

  const handleUpdateLimit = useCallback(
    (seatLimit: number | null) => {
      if (!selectedOrganization) return

      if (seatLimit === null) {
        // Remove limit
        removeLimit({
          variables: {
            organizationId: selectedOrganization.id,
          },
        })
      } else {
        // Update limit
        updateLimit({
          variables: {
            input: {
              organizationId: selectedOrganization.id,
              seatLimit,
            },
          },
        })
      }
    },
    [selectedOrganization, updateLimit, removeLimit]
  )

  const [linkOrganization] = useMutation(LINK_ORGANIZATION_TO_TENANT)

  const handleCreateOrganization = useCallback(
    async (data: { name: string; slug: string }) => {
      setCreating(true)
      try {
        // Create organization via Better Auth
        await createOrganization({
          name: data.name,
          slug: data.slug,
        })

        // Auto-link to tenant (tenant will be auto-created with slug as name)
        await linkOrganization({
          variables: {
            input: {
              name: data.name,
              slug: data.slug,
              // tenantId is null - will auto-create tenant
            },
          },
        })

        toaster.success({ title: "Organization created successfully" })
        setShowCreateDialog(false)
        refetchOrgs()
      } catch (error: any) {
        toaster.error({ title: "Failed to create organization", description: error.message })
      } finally {
        setCreating(false)
      }
    },
    [refetchOrgs, linkOrganization]
  )

  const handleSort = useCallback(
    (field: SortField) => {
      if (sortField === field) {
        setSortDirection(sortDirection === "asc" ? "desc" : "asc")
      } else {
        setSortField(field)
        setSortDirection("asc")
      }
    },
    [sortField, sortDirection]
  )

  const organizations: Organization[] = orgsData?.getAllOrganizations || []
  const tenants = tenantsData?.getAllTenants || []

  // Filter organizations based on search query
  const filteredOrganizations = useMemo(() => {
    if (!searchQuery.trim()) return organizations

    const query = searchQuery.toLowerCase()
    return organizations.filter(
      (org) => org.name.toLowerCase().includes(query) || org.slug.toLowerCase().includes(query)
    )
  }, [organizations, searchQuery])

  // Sort filtered organizations
  const sortedOrganizations = useMemo(() => {
    const sorted = [...filteredOrganizations]
    sorted.sort((a, b) => {
      let comparison = 0

      switch (sortField) {
        case "name":
          comparison = a.name.localeCompare(b.name)
          break
        case "members":
          comparison = a.memberCount - b.memberCount
          break
        case "created":
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          break
      }

      return sortDirection === "asc" ? comparison : -comparison
    })
    return sorted
  }, [filteredOrganizations, sortField, sortDirection])

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <FaSort />
    return sortDirection === "asc" ? <FaSortUp /> : <FaSortDown />
  }

  return (
    <Stack gap={4}>
      <HStack justify="space-between">
        <Text fontSize="lg" fontWeight="bold">
          Organisations
        </Text>
        <CreateButton size="sm" onClick={() => setShowCreateDialog(true)}>
          Create Organisation
        </CreateButton>
      </HStack>

      {/* Search Input */}
      <Box>
        <Input
          placeholder="Search organisations by name or slug..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="sm"
        />
        {searchQuery && (
          <Text fontSize="xs" mt={1}>
            {filteredOrganizations.length} {filteredOrganizations.length === 1 ? "result" : "results"}
          </Text>
        )}
      </Box>

      {orgsLoading ? (
        <Spinner />
      ) : organizations.length === 0 ? (
        <Text>No organisations yet. Create your first organisation.</Text>
      ) : sortedOrganizations.length === 0 ? (
        <Text>No organisations match your search.</Text>
      ) : (
        <Table.Root size="sm">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader cursor="pointer" onClick={() => handleSort("name")}>
                <HStack gap={1}>
                  <Text>Name</Text>
                  {getSortIcon("name")}
                </HStack>
              </Table.ColumnHeader>
              <Table.ColumnHeader>Slug</Table.ColumnHeader>
              <Table.ColumnHeader cursor="pointer" onClick={() => handleSort("members")}>
                <HStack gap={1}>
                  <Text>Members / Limit</Text>
                  {getSortIcon("members")}
                </HStack>
              </Table.ColumnHeader>
              <Table.ColumnHeader cursor="pointer" onClick={() => handleSort("created")}>
                <HStack gap={1}>
                  <Text>Created</Text>
                  {getSortIcon("created")}
                </HStack>
              </Table.ColumnHeader>
              <Table.ColumnHeader>Actions</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {sortedOrganizations.map((org) => {
              const isAtCapacity =
                org.seatLimit !== null && org.seatLimit !== undefined && org.memberCount >= org.seatLimit

              return (
                <Table.Row key={org.id}>
                  <Table.Cell>
                    <VStack align="start" gap={0}>
                      <HStack gap={2}>
                        <Text fontWeight="medium">{org.name}</Text>
                        {!org.enabled && (
                          <Badge colorPalette="gray" size="sm">
                            Disabled
                          </Badge>
                        )}
                      </HStack>
                      <HStack gap={1}>
                        <Text fontSize="xs" truncate maxW="200px">
                          {org.id}
                        </Text>
                        <IconButton
                          aria-label="Copy organisation ID"
                          size="xs"
                          variant="ghost"
                          onClick={() => copyToClipboard(org.id)}
                        >
                          <FaCopy />
                        </IconButton>
                      </HStack>
                    </VStack>
                  </Table.Cell>
                  <Table.Cell>
                    <Text fontSize="sm">{org.slug}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    <HStack gap={2}>
                      <Text fontSize="sm">
                        {org.memberCount} / {org.seatLimit || "Unlimited"}
                      </Text>
                      {isAtCapacity && (
                        <Badge colorPalette="red" size="sm">
                          At Capacity
                        </Badge>
                      )}
                    </HStack>
                  </Table.Cell>
                  <Table.Cell>
                    <Text fontSize="sm">{formatDate(org.createdAt)}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    <HStack gap={2} flexWrap="wrap">
                      <UpdateButton
                        context={{ label: "Manage Sections", description: "Manage sections for the organisation" }}
                        onClick={() => {
                          setSelectedOrganization(org)
                          setShowManageSectionsDialog(true)
                        }}
                      />
                      <UpdateButton
                        context={{
                          label: "Manage Features",
                          description: "Manage feature overrides for the organisation",
                        }}
                        onClick={() => {
                          setSelectedOrganization(org)
                          setShowManageOrgFeaturesDialog(true)
                        }}
                      />
                      <UpdateButton
                        context={{
                          label: "Manage Alerts",
                          description: "Assign or unassign alerts for the organisation",
                        }}
                        onClick={() => {
                          setSelectedOrganization(org)
                          setShowManageAlertsDialog(true)
                        }}
                      />
                      <UpdateButton
                        context={{ label: "Manage Limits", description: "Manage the limits for the organisation" }}
                        onClick={() => {
                          setSelectedOrganization(org)
                          setShowManageLimitsDialog(true)
                        }}
                      />
                      <UpdateButton
                        context={{
                          label: "Advanced",
                          description: "Manage the advanced settings for the organisation",
                        }}
                        onClick={() => {
                          setSelectedOrganization(org)
                          setShowAdvancedDialog(true)
                        }}
                      />
                    </HStack>
                  </Table.Cell>
                </Table.Row>
              )
            })}
          </Table.Body>
        </Table.Root>
      )}

      {/* Create Organization Dialog */}
      <Dialog
        title="Create Organization"
        open={showCreateDialog}
        setOpen={setShowCreateDialog}
        loading={creating}
        disabled={creating}
      >
        <CreateOrganizationDialogContent
          creating={creating}
          onSubmit={handleCreateOrganization}
          onCancel={() => setShowCreateDialog(false)}
        />
      </Dialog>

      {/* Manage Limits Dialog */}
      {selectedOrganization && showManageLimitsDialog && (
        <Dialog
          title={`Manage Limits - ${selectedOrganization.name}`}
          open={showManageLimitsDialog}
          setOpen={setShowManageLimitsDialog}
        >
          <ManageLimitsDialog
            organization={selectedOrganization}
            onClose={() => setShowManageLimitsDialog(false)}
            onUpdate={refetchOrgs}
          />
        </Dialog>
      )}

      {/* Manage Sections Dialog */}
      {selectedOrganization && showManageSectionsDialog && (
        <Dialog
          title={`Manage Sections - ${selectedOrganization.name}`}
          open={showManageSectionsDialog}
          setOpen={setShowManageSectionsDialog}
        >
          <ManageSectionsDialog
            organization={selectedOrganization}
            onClose={() => setShowManageSectionsDialog(false)}
            onUpdate={refetchOrgs}
          />
        </Dialog>
      )}

      {/* Manage Features Dialog */}
      {selectedOrganization && showManageOrgFeaturesDialog && (
        <Dialog
          title={`Manage Features - ${selectedOrganization.name}`}
          open={showManageOrgFeaturesDialog}
          setOpen={setShowManageOrgFeaturesDialog}
          size="lg"
        >
          <OrganizationFeaturesManager organization={selectedOrganization} />
        </Dialog>
      )}

      {/* Advanced Organization Settings Dialog */}
      {selectedOrganization && showAdvancedDialog && (
        <Dialog
          title={`Advanced Settings - ${selectedOrganization.name}`}
          open={showAdvancedDialog}
          setOpen={setShowAdvancedDialog}
        >
          <AdvancedOrganizationSettings
            organization={selectedOrganization}
            onClose={() => setShowAdvancedDialog(false)}
            onUpdate={refetchOrgs}
          />
        </Dialog>
      )}

      {/* Manage Alerts Dialog */}
      {selectedOrganization && showManageAlertsDialog && (
        <ManageAlertsDialog
          open={showManageAlertsDialog}
          organization={selectedOrganization}
          onClose={() => setShowManageAlertsDialog(false)}
        />
      )}
    </Stack>
  )
}
